import { Component, Inject, OnInit } from '@angular/core'
import { ConfirmDialogService, SaveForm, selectAllRentals, untilDestroy } from '@tokeet-frontend/tv3-platform'
import {
  AutomationViewGroup,
  AutomationFormData,
  AutomationHelpers,
  AutomationTypes,
  getAvailableAutomationEvents,
  selectAvailableAutomationChannels,
  AutomationServiceTypes,
  selectIsAddingAutomation,
  selectIsUpdatingAutomation,
  createAutomationTimeForm,
} from '@tokeet-frontend/automations'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import * as fromRoot from '@tv3/store/state'
import { map } from 'rxjs/operators'
import { combineLatest, Observable } from 'rxjs'
import { select, Store } from '@ngrx/store'
import { Actions, ofType } from '@ngrx/effects'
import {
  AddAutomationComplete,
  DeleteAutomation,
  DeleteAutomationComplete,
  PauseAutomation,
  UnPauseAutomation,
} from '@tokeet-frontend/automations'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import { getAutomationHandlersByType } from '@tokeet-frontend/automations'
import { AutomationDestroyableSaver } from '@tv3/containers/automations/automation-destroyable-saver'
import { ContractTemplate, CreateContractTemplateComplete } from '@tokeet-frontend/signature'
import { EditTemplateDrawerService } from '@tv3/containers/documents/templates/template-drawer/edit-template/edit-template-drawer.service'
import { AutomationTab } from '@tv3/containers/automations/tabs'

@Component({
  selector: 'app-document-automation-overlay',
  templateUrl: './document-automation-overlay.component.html',
  styleUrls: ['./document-automation-overlay.component.scss'],
})
export class DocumentAutomationOverlayComponent extends AutomationDestroyableSaver implements OnInit {
  tab = AutomationTab.Overview
  tabs = AutomationTab

  form: FormGroup = this.fb.group({
    status: [true],
    service: [AutomationServiceTypes.Advance, [Validators.required]],
    type: [AutomationTypes.Document, [Validators.required]],
    name: [],
    event: ['', [Validators.required]],
    channels: [],
    rentals: [],
    timeevent: createAutomationTimeForm(this.fb),
    // message
    handler: ['', [Validators.required]],
    template: ['', [Validators.required]],
    users: this.fb.array([]),
    guest: [false],
  })

  get eventCtrl() {
    return this.form.get('event') as FormControl
  }

  get handlerCtrl() {
    return this.form.get('handler') as FormControl
  }

  get templateCtrl() {
    return this.form.get('template') as FormControl
  }

  getStatusTooltip() {
    return (this.form.get('status').value ? $localize`Disable` : $localize`Enable`) + $localize` this automation`
  }

  handlers = getAutomationHandlersByType(AutomationTypes.Document)

  rentals$ = this.store.pipe(select(selectAllRentals))
  channels$ = this.store.pipe(select(selectAvailableAutomationChannels))
  events = getAvailableAutomationEvents(AutomationTypes.Document)

  isEdit = false

  get isDuplicate() {
    return this.data?.duplicate
  }

  get allowsChannel() {
    return AutomationHelpers.allowsChannel(this.form.get('event').value)
  }

  get allowsRental() {
    return AutomationHelpers.allowsRental(this.form.get('event').value)
  }

  get automationGroup() {
    return this.data.automationGroup
  }

  template: ContractTemplate = null

  isAdding$: Observable<boolean> = this.store.select(selectIsAddingAutomation)
  isUpdating$: Observable<boolean> = this.store.select(selectIsUpdatingAutomation)

  isSaving$: Observable<boolean> = combineLatest([this.isAdding$, this.isUpdating$]).pipe(
    map(([isAdding, isUpdating]) => isAdding || isUpdating)
  )

  constructor(
    public dialogRef: MatDialogRef<DocumentAutomationOverlayComponent>,
    private fb: FormBuilder,
    private actions: Actions,
    private confirmDialogService: ConfirmDialogService,
    store: Store<fromRoot.State>,
    private editTemplateDrawer: EditTemplateDrawerService,
    amplitudeService: AmplitudeService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      automationGroup?: AutomationViewGroup
      defaults?: Partial<AutomationFormData>
      duplicate?: boolean
    }
  ) {
    super(store, amplitudeService)
  }

  ngOnInit() {
    this.actions.pipe(ofType(CreateContractTemplateComplete), untilDestroy(this)).subscribe((data) => {
      this.templateCtrl.setValue(data.item.template_id)
    })

    if (this.automationGroup) {
      this.isEdit = !this.data.duplicate
      this.setForm(this.automationGroup)
    } else if (this.data.defaults) {
      this.form.patchValue(this.data.defaults)
    }

    this.actions.pipe(ofType(AddAutomationComplete, DeleteAutomationComplete), untilDestroy(this)).subscribe(() => {
      this.close()
    })
  }

  setForm(group: AutomationViewGroup) {
    this.form.patchValue({
      ...group,
      guest: AutomationHelpers.sendToGuest(group.items),
      status: !!group.status,
      name: group.attributes?.name,
      event: AutomationHelpers.getAutomationEvent(group.event),
      timeevent: {
        event: group.timeevent,
        hours: group?.settings?.hours,
        days: group?.settings?.days,
      },
    })
  }

  onTogglePause(checked: boolean) {
    if (!checked) {
      this.store.dispatch(PauseAutomation({ ids: this.automationGroup.ids }))
    } else {
      this.store.dispatch(UnPauseAutomation({ ids: this.automationGroup.ids }))
    }
  }

  @SaveForm()
  onSave(form: FormGroup) {
    this.save(form.getRawValue(), this.isEdit ? this.automationGroup : null)
  }

  onEditTemplate() {
    if (this.template) {
      this.editTemplateDrawer.open(this.template.template_id)
    }
  }

  onDelete() {
    this.confirmDialogService
      .confirm({
        title: $localize`Delete this automation?`,
        body: $localize`Are you sure you want to delete this automation?`,
      })
      .subscribe(() => {
        this.store.dispatch(DeleteAutomation({ ids: this.automationGroup.ids }))
      })
  }

  close() {
    this.dialogRef.close()
  }
}
